/**
 * Desktop input producer: WASD/arrows to move, mouse to look, via Pointer Lock.
 *
 * Writes into an `InputState` and nothing else — it has no idea a camera exists. The
 * look delta it accumulates is in raw screen pixels; sensitivity and inversion are
 * applied when the delta is consumed, so both producers mean the same thing by
 * "sensitivity".
 */

import { POCKET_SLOTS } from '../../game/pocket';
import { resetInput, type InputState, type Vec2 } from '../inputState';

export type MoveAction = 'forward' | 'back' | 'left' | 'right';
export type KeyAction = MoveAction | 'jump' | 'interact' | 'stow';

const KEY_BINDINGS: Readonly<Record<string, KeyAction>> = {
  KeyW: 'forward',
  ArrowUp: 'forward',
  KeyS: 'back',
  ArrowDown: 'back',
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  Space: 'jump',
  KeyE: 'interact',
  KeyF: 'stow',
};

/**
 * The number row and the numpad, one key per pocket slot. Only as many as the pocket has:
 * 4 upwards stay the browser's, where several of them switch tabs.
 */
const POCKET_SLOT_BINDINGS: Readonly<Record<string, number>> = Object.fromEntries(
  Array.from({ length: POCKET_SLOTS }, (_, slot) => [
    [`Digit${slot + 1}`, slot],
    [`Numpad${slot + 1}`, slot],
  ]).flat(),
);

/** Physical key code to pocket slot index, or null for a key that names no slot. */
export function keyToPocketSlot(code: string): number | null {
  return POCKET_SLOT_BINDINGS[code] ?? null;
}

/** Physical key code to game action, or null for keys the game does not own. */
export function keyToAction(code: string): KeyAction | null {
  return KEY_BINDINGS[code] ?? null;
}

/**
 * How long after a press of forward a second press still counts as asking for a run.
 * Comfortable at a keyboard, and short enough that two deliberate steps forward are two
 * steps rather than a sprint (design D2).
 */
export const DOUBLE_TAP_MS = 300;

/**
 * Whether a press of forward at `now` follows close enough on the one before it to be the
 * sprint gesture. Null for a first press, which can never be a second one.
 *
 * Kept as a rule of its own because the producer has two ways to feed it wrongly, both of
 * which look fine on screen: auto-repeat (`event.repeat`) is not a second tap, and W and
 * the up arrow are the same forward and must share the timestamp.
 */
export function isDoubleTap(previousForwardPressAt: number | null, now: number): boolean {
  return previousForwardPressAt !== null && now - previousForwardPressAt <= DOUBLE_TAP_MS;
}

/**
 * Held keys to an analog movement vector, normalised so that walking diagonally is not
 * faster than walking straight — the classic bug that lets players strafe-run.
 */
export function moveVectorFromKeys(held: ReadonlySet<KeyAction>): Vec2 {
  const x = (held.has('right') ? 1 : 0) - (held.has('left') ? 1 : 0);
  const y = (held.has('forward') ? 1 : 0) - (held.has('back') ? 1 : 0);

  const magnitude = Math.hypot(x, y);
  if (magnitude === 0) return { x: 0, y: 0 };

  return { x: x / magnitude, y: y / magnitude };
}

type Options = {
  state: InputState;
  /** The element that receives clicks and is put under pointer lock. */
  surface: HTMLElement;
  /** Pointer lock ended — the game should pause. */
  onPointerLockLost?: () => void;
  /** This browser has no Pointer Lock (iOS Safari); drag-to-look is in use instead. */
  onPointerLockUnavailable?: () => void;
};

/**
 * Start listening. Returns a detach function that removes every listener it added.
 */
export function attachKeyboardMouse({
  state,
  surface,
  onPointerLockLost,
  onPointerLockUnavailable,
}: Options): () => void {
  const held = new Set<KeyAction>();
  /** When forward was last pressed for real — auto-repeat does not move this. */
  let lastForwardPressAt: number | null = null;
  let dragging = false;
  let usingDragFallback = false;
  let skipNextMouseMove = false;

  const isLocked = () => document.pointerLockElement === surface;
  /** Look input is only meaningful while locked, or while dragging in the fallback. */
  const isLooking = () => isLocked() || dragging;

  const applyMove = () => {
    const move = moveVectorFromKeys(held);
    state.move.x = move.x;
    state.move.y = move.y;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    // Asked for before the action bindings because a digit is not an action: it names a
    // slot, and the slot count is a rule of the pocket rather than four more bindings.
    const slot = keyToPocketSlot(event.code);
    if (slot !== null) {
      if (!event.repeat) state.pocketSlot = slot;
      return;
    }

    const action = keyToAction(event.code);
    if (!action) return;

    if (action === 'interact') {
      // A press is a moment and a hold is a duration, from one key: the press picks a
      // thing up, the hold drags one. Auto-repeat is neither — it is the browser
      // restating a hold that is already recorded, and re-arming the edge from it would
      // make one long press act again every few milliseconds.
      if (!event.repeat) state.interact = true;
      state.interactHeld = true;
    }
    else if (action === 'stow') {
      // An edge like the others: holding F is not three stows, it is one.
      if (!event.repeat) state.stow = true;
    } else if (action === 'jump') state.jump = true;
    else {
      // Tap, tap, and hold: the second press asks for a run, and it lasts as long as the
      // key does. `event.repeat` is the browser saying "still held", not a new press, so
      // it neither starts a sprint nor counts towards the next one.
      if (action === 'forward' && !event.repeat) {
        const now = performance.now();
        if (isDoubleTap(lastForwardPressAt, now)) state.sprint = true;
        lastForwardPressAt = now;
      }

      held.add(action);
      applyMove();
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const action = keyToAction(event.code);
    if (!action) return;

    if (action === 'jump') state.jump = false;
    else if (action === 'interact') state.interactHeld = false;
    // Nothing to release for a stow: the edge was consumed the moment it was pressed.
    else if (action === 'stow') return;
    else {
      // Letting go of forward ends the run, the same way it ends the movement. Turning
      // means tapping again, which is the convention everywhere this gesture appears.
      if (action === 'forward') state.sprint = false;
      held.delete(action);
      applyMove();
    }
  };

  /**
   * No human flick produces a bigger single-event delta; the browser bugs this guards
   * against produce deltas in the thousands.
   */
  const MAX_MOVEMENT_PER_EVENT = 300;
  const clampMovement = (delta: number) =>
    Math.max(-MAX_MOVEMENT_PER_EVENT, Math.min(MAX_MOVEMENT_PER_EVENT, delta));

  const onMouseMove = (event: MouseEvent) => {
    if (!isLooking()) return;
    // Chromium (notably on Linux) reports the absolute cursor jump as the first
    // movement after pointer lock engages. Applying it slams the view into the pitch
    // clamp — straight up — and every throw goes skyward. Discard that first event.
    if (skipNextMouseMove) {
      skipNextMouseMove = false;
      return;
    }
    state.look.x += clampMovement(event.movementX);
    state.look.y += clampMovement(event.movementY);
  };

  const onClick = () => {
    if (isLocked() || dragging) return;

    // Not every browser has Pointer Lock — iOS Safari has none at all. Where it is
    // missing, press-and-drag is the fallback rather than "desktop is unsupported".
    if (typeof surface.requestPointerLock !== 'function') {
      if (!usingDragFallback) {
        usingDragFallback = true;
        onPointerLockUnavailable?.();
      }
      return;
    }

    try {
      void surface.requestPointerLock();
    } catch {
      if (!usingDragFallback) {
        usingDragFallback = true;
        onPointerLockUnavailable?.();
      }
    }
  };

  const onPointerLockChange = () => {
    if (isLocked()) {
      skipNextMouseMove = true;
      return;
    }
    // Escape, an alt-tab, or the browser deciding for us. Whatever the reason, stop
    // moving: a key held at this moment will never receive its keyup.
    held.clear();
    lastForwardPressAt = null;
    resetInput(state);
    onPointerLockLost?.();
  };

  const onMouseDown = (event: MouseEvent) => {
    // While locked, the left button both throws and interacts: with full hands it
    // hurls the box, with empty hands it acts on whatever is under the crosshair — the
    // same click-on-a-thing the pad has. Which one happens is decided downstream, by
    // the systems that know whether anything is being carried; a producer does not
    // read game state (CLAUDE.md, architectural rule 2).
    //
    // While not locked, a press either starts the drag-look fallback or is part of the
    // click that acquires pointer lock — never a throw, or acquiring lock would also
    // hurl whatever the player carries.
    if (isLocked()) {
      if (event.button === 0) {
        state.throw = true;
        state.interact = true;
      }
      return;
    }
    if (usingDragFallback) dragging = true;
  };

  const onMouseUp = () => {
    dragging = false;
  };

  const onBlur = () => {
    held.clear();
    lastForwardPressAt = null;
    resetInput(state);
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('pointerlockchange', onPointerLockChange);
  document.addEventListener('mouseup', onMouseUp);
  surface.addEventListener('click', onClick);
  surface.addEventListener('mousedown', onMouseDown);

  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('pointerlockchange', onPointerLockChange);
    document.removeEventListener('mouseup', onMouseUp);
    surface.removeEventListener('click', onClick);
    surface.removeEventListener('mousedown', onMouseDown);
    held.clear();
    lastForwardPressAt = null;
    resetInput(state);
  };
}
